import { IonSpinner } from '@ionic/react';
import React from 'react';
import { createPortal } from 'react-dom';

export type CloudSavePhase =
  | 'matching'
  | 'creating'
  | 'adding'
  | 'done'
  | 'error';

export interface UnmatchedCloudTrack {
  artist: string;
  title: string;
  reason: string;
}

interface CloudSaveStatusPanelProps {
  visible: boolean;
  serviceLabel: string;
  status: string;
  currentTrack: string;
  matchedCount: number;
  totalCount: number;
  phase: CloudSavePhase;
  unmatched: UnmatchedCloudTrack[];
  onClose: () => void;
  onCopyUnmatched?: () => void;
}

export const CloudSaveStatusPanel: React.FC<CloudSaveStatusPanelProps> = ({
  visible,
  serviceLabel,
  status,
  currentTrack,
  matchedCount,
  totalCount,
  phase,
  unmatched,
  onClose,
  onCopyUnmatched,
}) => {
  if (!visible) {
    return null;
  }

  const unmatchedCount = unmatched.length;
  const showUnmatched = phase === 'done' && unmatchedCount > 0;

  return createPortal(
    <div
      className={`cloud-save-status-panel ${phase}${showUnmatched ? ' partial' : ''}`}
    >
      <div className="status-header">
        <span className="status-service">{serviceLabel}</span>
        {phase !== 'done' && phase !== 'error' && (
          <IonSpinner name="dots" className="status-spinner" />
        )}
        {phase === 'done' && <span className="status-success">✓</span>}
        {phase === 'error' && <span className="status-error">✗</span>}
        <button className="status-close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="status-message">{status}</div>
      {currentTrack && <div className="status-track">{currentTrack}</div>}
      {phase === 'matching' && (
        <div className="status-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${totalCount ? (matchedCount / totalCount) * 100 : 0}%`,
              }}
            />
          </div>
          <span className="progress-count">
            {matchedCount}/{totalCount} matched
          </span>
        </div>
      )}
      {showUnmatched && (
        <div className="status-unmatched">
          <div className="status-unmatched__header">
            <span>{unmatchedCount} not found</span>
            {onCopyUnmatched && (
              <button type="button" onClick={onCopyUnmatched}>
                Copy list
              </button>
            )}
          </div>
          <ul className="status-unmatched__list">
            {unmatched.map((track, index) => (
              <li key={`${track.artist}-${track.title}-${index}`}>
                <span className="status-unmatched__track">
                  {track.artist} – {track.title}
                </span>
                <span className="status-unmatched__reason">{track.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>,
    document.body,
  );
};
